import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useRealtimeInventory } from "@/hooks/dashboard/useRealtimeInventory";
import { AlertTriangle, Package, TrendingDown } from "lucide-react";

interface InventoryAlertsChartProps {
  timeRange: string;
  refreshInterval: number;
}

const InventoryAlertsChart: React.FC<InventoryAlertsChartProps> = ({
  refreshInterval
}) => {
  const { inventoryAlerts, inventoryStats, isLoading } = useRealtimeInventory(refreshInterval);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "critical":
        return "destructive";
      case "low":
        return "secondary";
      case "warning":
        return "outline";
      default:
        return "outline";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "critical":
        return <AlertTriangle className="w-4 h-4" />;
      case "low":
        return <TrendingDown className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Voorraad Alerts</CardTitle>
            <CardDescription>Lage voorraad waarschuwingen</CardDescription>
          </div>
          <AlertTriangle className="h-4 w-4 text-muted-foreground animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Voorraad Alerts</CardTitle>
          <CardDescription>
            {inventoryAlerts.length} items met lage voorraad
          </CardDescription>
        </div>
        <AlertTriangle className="h-4 w-4 text-green-500 animate-pulse" />
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-3 bg-muted rounded-lg">
            <div className="text-2xl font-bold text-destructive">
              {inventoryStats.criticalItems}
            </div>
            <div className="text-xs text-muted-foreground">Kritiek</div>
          </div>
          <div className="text-center p-3 bg-muted rounded-lg">
            <div className="text-2xl font-bold text-warning">
              {inventoryStats.lowStockItems}
            </div>
            <div className="text-xs text-muted-foreground">Laag</div>
          </div>
        </div>

        {/* Alerts List */}
        <ScrollArea className="h-[200px]">
          <div className="space-y-2">
            {inventoryAlerts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Geen voorraad alerts</p>
              </div>
            ) : (
              inventoryAlerts.map((alert, index) => (
                <div key={index} className="flex items-center justify-between p-2 border rounded-lg">
                  <div className="flex items-center gap-2 min-w-0">
                    {getStatusIcon(alert.status)}
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">
                        {alert.product_naam}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {alert.product_id} • {alert.magazijn}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-right">
                      <div className="font-medium text-sm">
                        {alert.voorraad}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        MOQ: {alert.moq}
                      </div>
                    </div>
                    <Badge variant={getStatusColor(alert.status)} className="text-xs">
                      {alert.status}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Footer Stats */}
        <div className="flex justify-between mt-4 pt-4 border-t text-sm text-muted-foreground">
          <span>Totaal producten: {inventoryStats.totalProducts}</span>
          <span>Uitverkocht: {inventoryStats.outOfStockItems}</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default InventoryAlertsChart;